import { Component, ComponentInterface, Element, h, Host, JSX, Prop, Watch } from '@stencil/core';
import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { PopoverAlignment, PopoverPosition } from './popover.types';
import { DEFAULT_POPOVER_ALIGNMENT, DEFAULT_POPOVER_POSITION } from './popover.constants';
import { getViewportWidth } from '../../../utils/get-viewport-width';
import { MEDIA_QUERY_SIZES } from '../../../utils/services/media-query.service';

/**
 * @slot - Element that triggers the popover like a button or anchor
 * @slot content - Content to be displayed in the popover overlay
 */
@Component({
  tag: 'wb-popover',
  styleUrl: './popover.scss',
  shadow: true,
})
export class PopoverComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** If present, shows overlay */
  @Prop({ reflect: true, mutable: true })
  public open: boolean = false;

  /** Position of the popover */
  @Prop({ reflect: true })
  public position: PopoverPosition = DEFAULT_POPOVER_POSITION;

  /** Horizontal position of the popover relative to the trigger element (only takes effect from MQ3 upwards) */
  @Prop({ reflect: true })
  public horizontalAlignment: PopoverAlignment = DEFAULT_POPOVER_ALIGNMENT;

  /** Makes popover have recommended card */
  @Prop({ reflect: true })
  public padded: boolean = false;

  @Watch('position')
  public onConfigurationChange(): void {
    this.updateOverlay();
  }

  @Watch('horizontalAlignment')
  public onHorizontalAlignmentChange(): void {
    this.updateOverlay();
  }

  @Watch('open')
  public onOpenChange(): void {
    this.open //
      ? this.addOpenListeners()
      : this.removeOpenListeners();
  }

  private overlayEl: HTMLElement;
  private triggerEl: HTMLElement;

  private cleanup: () => void;

  public componentDidLoad(): void {
    if (this.open) {
      this.addOpenListeners();
    }
  }

  private updateOverlay = async () => {
    let preferedPlacement;
    const horizontalAlignmentSuffix = this.horizontalAlignment === DEFAULT_POPOVER_ALIGNMENT ? '' : `-${this.horizontalAlignment}`;

    if (getViewportWidth() < MEDIA_QUERY_SIZES.mq3) {
      preferedPlacement = this.position; // on small viewports the tooltip should always be centered
    } else {
      preferedPlacement = this.position + horizontalAlignmentSuffix;
    }

    const popoverPosition = getComputedStyle(this.el).getPropertyValue('--popover-position');

    const { x, y } = await computePosition(this.triggerEl, this.overlayEl, {
      placement: preferedPlacement,
      strategy: popoverPosition !== 'fixed' ? 'absolute' : 'fixed',
      middleware: [
        offset(8), //
        flip({
          padding: 8,
          fallbackAxisSideDirection: 'start',
        }),
        shift({ padding: 16 }),
      ],
    });

    Object.assign(this.overlayEl.style, {
      left: `${x}px`,
      top: `${y}px`,
    });
  };

  // note: the focusout event behaves quite unexpectedly in combination with Shadow DOM
  private isOverlayStillFocussed = (evt: FocusEvent): boolean => {
    const elementReceivingFocus = evt.relatedTarget as HTMLElement;

    // case 1: no element receiving focus -> keep it open
    if (!elementReceivingFocus) {
      return true;
    }

    // case 2: user tabbed back to popup trigger element -> close popover
    if (elementReceivingFocus === this.triggerEl) {
      return false;
    }

    // case 3: user shifts focus using TAB -> only close overlay if element receiving focus is outside the overlay
    return evt.composedPath().includes(this.el);
  };

  private onOverlayFocusOut = (evt: FocusEvent): void => {
    if (this.open && !this.isOverlayStillFocussed(evt)) {
      this.closeOverlay();
    }
  };

  private openOverlay = (): void => {
    this.open = true;
  };

  private closeOverlay = (): void => {
    this.open = false;
  };

  private toggleOverlay = (): void => {
    this.open ? this.closeOverlay() : this.openOverlay();
  };

  private addOpenListeners = (): void => {
    window.addEventListener('mousedown', this.closeIfClickOutside);
    window.addEventListener('touchstart', this.closeIfClickOutside);

    this.cleanup = autoUpdate(this.triggerEl, this.overlayEl, this.updateOverlay, {
      ancestorScroll: getComputedStyle(this.el).getPropertyValue('--popover-position') === 'fixed',
    });
  };

  private removeOpenListeners = (): void => {
    window.removeEventListener('mousedown', this.closeIfClickOutside);
    window.removeEventListener('touchstart', this.closeIfClickOutside);
    this.cleanup();
  };

  private closeIfClickOutside = (evt: Event): void => {
    const path = evt.composedPath();
    const isClickOnTrigger = path.includes(this.triggerEl);

    if (isClickOnTrigger) {
      return;
    }

    const isClickInside = path.includes(this.el);

    if (!isClickInside) {
      this.closeOverlay();
    }
  };

  private updateTriggerEl = (evt: Event): void => {
    const slottedElements = (evt.target as HTMLSlotElement).assignedElements();

    if (slottedElements.length !== 1) {
      console.warn(`wb-popover: exactly one trigger element must be slotted, got ${slottedElements.length}`, evt.target);
      if (!slottedElements.length) {
        return;
      }
    }

    this.triggerEl = slottedElements[0] as HTMLElement;
    this.triggerEl.addEventListener('click', this.toggleOverlay);
  };

  private bindOverlay = (el: HTMLElement): void => {
    this.overlayEl = el;
  };

  public render(): JSX.Element {
    return (
      <Host class="wb-popover" onFocusout={this.onOverlayFocusOut}>
        <slot onSlotchange={this.updateTriggerEl} />
        <div class="overlay" ref={this.bindOverlay} data-testid="wb-popover-overlay">
          {this.open && <slot name="content" onSlotchange={this.updateOverlay} />}
        </div>
      </Host>
    );
  }
}
